import React, { useContext, useState } from 'react';
import { navigate, useParams } from '@reach/router';
import { ButtonVariant } from 'react-magma-dom';
import { MenuBookIcon, PlayArrowIcon } from 'react-magma-icons';
import { ErrorDownloadModal } from '../Modal/ErrorDownloadModal';
import {
  StyledMoreCenterBlock,
  StyledMoreInlineBlock,
  StyledMoreSubTitle,
  StyledMoreTitle,
} from '../Styled/StyledMore';
import { EditToolContext } from '../../contexts/editToolContext';
import { getStageColors, handleDownloadFile } from '../../utils';
import { COLORS } from '../../constants';
import { Assets, IAsset } from '../../constants/assets';
import { DownloadIcon } from '../../assets/icons/Download';

interface IMoreItem {
  asset: IAsset;
  onShowVideo: (asset: IAsset) => void;
}

export const MoreItem = (props: IMoreItem): JSX.Element => {
  const { asset, onShowVideo } = props;
  const { type, title, description, links } = asset;

  const [isLoading, setIsLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const { handleLeavePage } = useContext(EditToolContext);
  const { stage } = useParams();

  const { main: mainColor } = getStageColors(stage);

  const openErrorModal = () => {
    setIsErrorModalOpen(true);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const handleVideoClick = () => {
    onShowVideo(asset);
  };

  const handlePDFClick = () => {
    !isLoading && handleDownloadFile(title, links.self, setIsLoading, openErrorModal);
  };

  const handleDefaultClick = () => {
    const needStayOnPage = handleLeavePage(() => navigate(links.self), false);
    if (!needStayOnPage && type === 'IWP') {
      navigate(links.self);
    }
  };

  const handleClick = () => {
    switch (type) {
      case Assets.video:
        return handleVideoClick();
      case Assets.pdf:
        return handlePDFClick();
      default:
        return handleDefaultClick();
    }
  };

  const renderIcon = () => {
    switch (type) {
      case Assets.video:
        return <PlayArrowIcon size={32} color={COLORS.seaBlue} />;
      case Assets.pdf:
        return (
          <DownloadIcon
            isLoading={isLoading}
            size={32}
            spinner={{
              size: 36,
              color: COLORS.seaBlue,
              margin: '12px',
            }}
          />
        );
      default:
        return <MenuBookIcon size={32} color={COLORS.seaBlue} />;
    }
  };

  return (
    <>
      <StyledMoreInlineBlock onClick={handleClick}>
        <StyledMoreCenterBlock variant={ButtonVariant.solid} disabled={isLoading}>
          {renderIcon()}
        </StyledMoreCenterBlock>
        <StyledMoreTitle data-testid="more-item">{title}</StyledMoreTitle>
        <StyledMoreSubTitle>{description}</StyledMoreSubTitle>
      </StyledMoreInlineBlock>
      {isErrorModalOpen && (
        <ErrorDownloadModal
          mainColor={mainColor}
          onCloseClick={closeErrorModal}
          onRetryClick={() => {
            closeErrorModal();
            handlePDFClick();
          }}
        />
      )}
    </>
  );
};
